import { vec2 } from 'gl-matrix';

export type DicomWindowComponent = number | string | Array<number | string>;
export interface WindowingPreset {
    name: string;
    ww: number;
    wc: number;
    disabled?: boolean;
}

export const WINDOWING_PRESETS: WindowingPreset[] = [
    { name: 'Abdomen', ww: 400, wc: 40 },
    { name: 'Bone', ww: 1600, wc: 500 },
    { name: 'Brain', ww: 85, wc: 30 },
    { name: 'Breast', ww: 400, wc: -50 },
    { name: 'Dental', ww: 3000, wc: 900 },
    { name: 'Liver', ww: 160, wc: 50 },
    { name: 'Lung', ww: 1600, wc: -600 },
    { name: 'Metal', ww: 1, wc: 2800 },
    { name: 'Neck', ww: 325, wc: 65 },
    { name: 'Pelvis', ww: 500, wc: 0 },
    { name: 'Prostate', ww: 210, wc: -35 },
    { name: 'Soft Tissue', ww: 350, wc: 40 },
];

/**
 * Simple class to store window level values for an image
 */
export class WindowLevel {
    /**
     * 	(0028,1051) Window Width Attribute, Corresponding to DICOM attribute by default
     */
    /**
     * 	(0028,1050) Window Center Attribute, Corresponding to DICOM attribute by default
     */
    public constructor(
        public readonly window: number,
        public readonly level: number,
        public readonly minLevel = -1000,
        public readonly maxLevel = 65536
    ) {
        if (window <= 0) {
            throw new Error('Window must be greater than 0');
        }
        if (level < minLevel || level > maxLevel) {
            throw new Error(
                `Level must be between ${minLevel} and ${maxLevel}`
            );
        }
    }

    public clone(): WindowLevel {
        return new WindowLevel(
            this.window,
            this.level,
            this.minLevel,
            this.maxLevel
        );
    }

    public equals(other: WindowLevel): boolean {
        return (
            this.window === other.window &&
            this.level === other.level &&
            this.minLevel === other.minLevel &&
            this.maxLevel === other.maxLevel
        );
    }

    public get asVec2(): vec2 {
        return vec2.fromValues(this.window, this.level);
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(value, max));
    }

    public add(increment: vec2): WindowLevel {
        return new WindowLevel(
            Math.round(
                this.clamp(
                    this.window + increment[0],
                    1,
                    this.maxLevel - this.minLevel
                )
            ),
            Math.round(
                this.clamp(
                    this.level + increment[1],
                    this.minLevel,
                    this.maxLevel
                )
            ),
            this.minLevel,
            this.maxLevel
        );
    }
}

/**
 * Computes an automatic window/level based on histogram analysis of pixel data.
 * The algorithm samples the pixel data to build a histogram, then finds the range
 * encompassing the central 90% of pixel values to determine window/level.
 */
export function computeAutoWindowLevelFromHistogram(
    data: Int16Array | Float32Array,
    sizeX?: number,
    sizeY?: number,
    sizeZ?: number,
    sampleStepX = 4,
    sampleStepY = 4,
    sampleStepZ = 2
): WindowLevel {
    const histogram = new Map<number, number>();
    let datalen = 0;
    // If sizes are not provided, fall back to full sampling (legacy behavior)
    if (
        sizeX === undefined ||
        sizeY === undefined ||
        sizeZ === undefined ||
        sizeX * sizeY * sizeZ !== data.length
    ) {
        for (const d of data) {
            histogram.set(d, (histogram.get(d) ?? 0) + 1);
        }
        datalen = data.length;
    } else {
        // 3D grid sampling using strides (sampleStepX/Y/Z)
        const sx = Math.max(1, Math.floor(sampleStepX));
        const sy = Math.max(1, Math.floor(sampleStepY));
        const sz = Math.max(1, Math.floor(sampleStepZ));
        const sliceSize = sizeX * sizeY;

        for (let z = 0; z < sizeZ; z += sz) {
            const zOffset = z * sliceSize;
            for (let y = 0; y < sizeY; y += sy) {
                const yOffset = y * sizeX;
                for (let x = 0; x < sizeX; x += sx) {
                    const idx = zOffset + yOffset + x;
                    const d = data[idx];
                    histogram.set(d, (histogram.get(d) ?? 0) + 1);
                    datalen += 1;
                }
            }
        }
    }

    const imagePixels = datalen;
    // cutoff a portion of the histogram to avoid individual pixels at the edges of the histogram affecting the window level
    const minCutoff = Math.floor(imagePixels * 0.05);
    const maxCutoff = Math.floor(imagePixels * 0.995);
    let minCount = 0;
    let maxCount = 0;
    let minIdx = 0,
        maxIdx = 0;

    const sortedHistogram = Array.from(histogram.entries()).sort(
        (a, b) => a[0] - b[0]
    );

    for (const [key, value] of sortedHistogram) {
        if (minCount < minCutoff) {
            minCount += value;
            minIdx = key;
        }
        if (maxCount < maxCutoff) {
            maxCount += value;
            maxIdx = key;
        }
    }
    if (minIdx === maxIdx) {
        // avoid zero window
        maxIdx = minIdx + 1;
    }

    return new WindowLevel(maxIdx - minIdx, (minIdx + maxIdx) * 0.5);
}

/**
 * Shared helper: sample a 3D pixel volume using grid strides.
 * Returns a flat Float64Array of sampled values.
 */
function samplePixelData(
    data: Int16Array | Float32Array,
    sizeX?: number,
    sizeY?: number,
    sizeZ?: number,
    sampleStepX = 4,
    sampleStepY = 4,
    sampleStepZ = 2
): Float64Array {
    if (
        sizeX === undefined ||
        sizeY === undefined ||
        sizeZ === undefined ||
        sizeX * sizeY * sizeZ !== data.length
    ) {
        return new Float64Array(data);
    }
    const sx = Math.max(1, Math.floor(sampleStepX));
    const sy = Math.max(1, Math.floor(sampleStepY));
    const sz = Math.max(1, Math.floor(sampleStepZ));
    const sliceSize = sizeX * sizeY;
    const estimatedSize =
        Math.ceil(sizeX / sx) * Math.ceil(sizeY / sy) * Math.ceil(sizeZ / sz);
    const result = new Float64Array(estimatedSize);
    let idx = 0;
    for (let z = 0; z < sizeZ; z += sz) {
        const zOffset = z * sliceSize;
        for (let y = 0; y < sizeY; y += sy) {
            const yOffset = y * sizeX;
            for (let x = 0; x < sizeX; x += sx) {
                result[idx++] = data[zOffset + yOffset + x];
            }
        }
    }
    return result.subarray(0, idx);
}

/**
 * Find a threshold using Otsu's method on a set of pixel values.
 * Returns the threshold that maximizes between-class variance.
 */
function computeOtsuThreshold(values: Float64Array, numBins = 256): number {
    let minVal = values[0];
    let maxVal = values[0];
    for (let i = 1; i < values.length; i++) {
        if (values[i] < minVal) {
            minVal = values[i];
        }
        if (values[i] > maxVal) {
            maxVal = values[i];
        }
    }
    if (minVal === maxVal) {
        return minVal;
    }

    const binWidth = (maxVal - minVal) / numBins;
    const counts = new Float64Array(numBins);
    for (let i = 0; i < values.length; i++) {
        const bin = Math.min(
            Math.floor((values[i] - minVal) / binWidth),
            numBins - 1
        );
        counts[bin]++;
    }

    const total = values.length;
    let sumTotal = 0;
    for (let i = 0; i < numBins; i++) {
        sumTotal += (minVal + (i + 0.5) * binWidth) * counts[i];
    }

    let sumBg = 0;
    let weightBg = 0;
    let maxVariance = 0;
    let bestBin = 0;

    for (let i = 0; i < numBins; i++) {
        weightBg += counts[i];
        if (weightBg === 0) {
            continue;
        }
        const weightFg = total - weightBg;
        if (weightFg === 0) {
            break;
        }
        sumBg += (minVal + (i + 0.5) * binWidth) * counts[i];
        const meanBg = sumBg / weightBg;
        const meanFg = (sumTotal - sumBg) / weightFg;
        const variance = weightBg * weightFg * (meanBg - meanFg) ** 2;
        if (variance > maxVariance) {
            maxVariance = variance;
            bestBin = i;
        }
    }

    return minVal + (bestBin + 0.5) * binWidth;
}

/**
 * Compute percentile value from a Float64Array.
 * Clones and sorts the array to find the value at the given percentile.
 */
function percentile(values: Float64Array, p: number): number {
    const sorted = Float64Array.from(values).sort();
    const idx = Math.min(Math.floor(p * sorted.length), sorted.length - 1);
    return sorted[idx];
}

// ---- MR Auto Window/Level Algorithms ----

/**
 * MR Auto WL: Otsu threshold to separate background from foreground,
 * then use 2nd/98th percentile on foreground pixels.
 */
export function computeAutoWindowLevelMrOtsu(
    data: Int16Array | Float32Array,
    sizeX?: number,
    sizeY?: number,
    sizeZ?: number,
    sampleStepX = 4,
    sampleStepY = 4,
    sampleStepZ = 2
): WindowLevel {
    const sampled = samplePixelData(
        data,
        sizeX,
        sizeY,
        sizeZ,
        sampleStepX,
        sampleStepY,
        sampleStepZ
    );

    const threshold = computeOtsuThreshold(sampled);

    // Keep only foreground pixels
    let fgCount = 0;
    for (let i = 0; i < sampled.length; i++) {
        if (sampled[i] > threshold) {
            sampled[fgCount++] = sampled[i];
        }
    }
    const foreground = fgCount > 0 ? sampled.subarray(0, fgCount) : sampled;

    const p2 = percentile(foreground, 0.02);
    const p98 = percentile(foreground, 0.98);
    const minVal = p2;
    const maxVal = p2 === p98 ? p2 + 1 : p98;

    return new WindowLevel(
        Math.round(maxVal - minVal),
        Math.round((minVal + maxVal) * 0.5)
    );
}

// ---- Diagnostic-style (wide/flat contrast) MR Algorithms ----

/**
 * Helper: exclude background pixels using Otsu threshold,
 * return only foreground values as a subarray.
 */
function excludeBackground(sampled: Float64Array): Float64Array {
    const threshold = computeOtsuThreshold(sampled);
    let fgCount = 0;
    for (let i = 0; i < sampled.length; i++) {
        if (sampled[i] > threshold) {
            sampled[fgCount++] = sampled[i];
        }
    }
    return fgCount > 0 ? sampled.subarray(0, fgCount) : sampled;
}

/**
 * MR Auto WL: Diagnostic wide window — Otsu to exclude background,
 * then use the full min-to-max range of signal pixels.
 * Produces the widest possible window for maximum tissue visibility.
 */
export function computeAutoWindowLevelMrDiagFullRange(
    data: Int16Array | Float32Array,
    sizeX?: number,
    sizeY?: number,
    sizeZ?: number,
    sampleStepX = 4,
    sampleStepY = 4,
    sampleStepZ = 2
): WindowLevel {
    const sampled = samplePixelData(
        data,
        sizeX,
        sizeY,
        sizeZ,
        sampleStepX,
        sampleStepY,
        sampleStepZ
    );
    const fg = excludeBackground(sampled);
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < fg.length; i++) {
        if (fg[i] < lo) {
            lo = fg[i];
        }
        if (fg[i] > hi) {
            hi = fg[i];
        }
    }
    const ww = Math.max(Math.round(hi - lo), 1);
    const wc = Math.round((lo + hi) * 0.5);
    return new WindowLevel(ww, wc);
}

/** Named MR auto WL algorithm entry for UI display. */
export interface MrAutoWindowLevelAlgorithm {
    name: string;
    compute: (
        data: Int16Array | Float32Array,
        sizeX?: number,
        sizeY?: number,
        sizeZ?: number
    ) => WindowLevel;
}

/** All available MR auto window/level algorithms for testing. */
export const MR_AUTO_WL_ALGORITHMS: MrAutoWindowLevelAlgorithm[] = [
    {
        name: 'Auto (Otsu)',
        compute: computeAutoWindowLevelMrOtsu,
    },
    {
        name: 'Auto (Diag Full Range)',
        compute: computeAutoWindowLevelMrDiagFullRange,
    },
];

/**
 * Computes an automatic window/level optimized for CT datasets.
 * CT images use Hounsfield Units (HU) with known reference points:
 * - Air: -1000 HU
 * - Water: 0 HU
 * - Soft tissue: ~40-80 HU
 * - Bone: 400-1000+ HU
 *
 * This function excludes air and very high density values (metal artifacts)
 * and uses tighter percentile cutoffs to focus on clinically relevant tissue.
 */
export function computeAutoWindowLevelForCT(
    data: Int16Array | Float32Array,
    sizeX?: number,
    sizeY?: number,
    sizeZ?: number,
    sampleStepX = 4,
    sampleStepY = 4,
    sampleStepZ = 2,
    options?: {
        minHU?: number;
        maxHU?: number;
        lowerPercentile?: number;
        upperPercentile?: number;
    }
): WindowLevel {
    const minHU = options?.minHU ?? -200;
    const maxHU = options?.maxHU ?? 2000;
    const lowerPercentile = options?.lowerPercentile ?? 0.02;
    const upperPercentile = options?.upperPercentile ?? 0.98;

    const histogram = new Map<number, number>();
    let datalen = 0;

    // Helper to add value to histogram if within CT tissue range
    const addToHistogram = (value: number): void => {
        if (value >= minHU && value <= maxHU) {
            histogram.set(value, (histogram.get(value) ?? 0) + 1);
            datalen += 1;
        }
    };

    // If sizes are not provided, fall back to full sampling (legacy behavior)
    if (
        sizeX === undefined ||
        sizeY === undefined ||
        sizeZ === undefined ||
        sizeX * sizeY * sizeZ !== data.length
    ) {
        for (const d of data) {
            addToHistogram(d);
        }
    } else {
        // 3D grid sampling using strides (sampleStepX/Y/Z)
        const sx = Math.max(1, Math.floor(sampleStepX));
        const sy = Math.max(1, Math.floor(sampleStepY));
        const sz = Math.max(1, Math.floor(sampleStepZ));
        const sliceSize = sizeX * sizeY;

        for (let z = 0; z < sizeZ; z += sz) {
            const zOffset = z * sliceSize;
            for (let y = 0; y < sizeY; y += sy) {
                const yOffset = y * sizeX;
                for (let x = 0; x < sizeX; x += sx) {
                    const idx = zOffset + yOffset + x;
                    addToHistogram(data[idx]);
                }
            }
        }
    }

    // Fallback if no valid CT data found
    if (datalen === 0) {
        // Return a reasonable default soft tissue window
        return new WindowLevel(400, 40);
    }

    const minCutoff = Math.floor(datalen * lowerPercentile);
    const maxCutoff = Math.floor(datalen * upperPercentile);
    let minCount = 0;
    let maxCount = 0;
    let minIdx = minHU;
    let maxIdx = maxHU;

    const sortedHistogram = Array.from(histogram.entries()).sort(
        (a, b) => a[0] - b[0]
    );

    for (const [key, value] of sortedHistogram) {
        if (minCount < minCutoff) {
            minCount += value;
            minIdx = key;
        }
        if (maxCount < maxCutoff) {
            maxCount += value;
            maxIdx = key;
        }
    }

    if (minIdx === maxIdx) {
        // avoid zero window
        maxIdx = minIdx + 1;
    }

    // Ensure minimum window width for CT (at least 50 HU for visibility)
    const windowWidth = Math.max(50, maxIdx - minIdx);
    const windowCenter = Math.round((minIdx + maxIdx) * 0.5);

    return new WindowLevel(windowWidth, windowCenter);
}
